import React, { useState } from 'react'
import MainLayout from 'components/mainLayout'
import AttachmentPreview from 'components/attachment'
import { Breadcrumb, Empty, Layout, PageHeader, Pagination, Spin } from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	getReports,
	reprocessReport,
	checkReportStatus,
} from 'services/reports/reportsService'
import { errorMessage, successMessage } from 'services/messager'
import { formatDate, formatDateKey, formatFileSize } from 'utils/formatters'

import styles from './index.module.scss'

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100']
const REPORT_TYPE_LABELS = {
	documents: 'Relatório de Documentos',
	demands: 'Relatório de Demandas',
}

function groupReportsByDate(reports) {
	const groups = {}
	for (const report of reports) {
		const key = formatDateKey(report.created_at)
		if (!groups[key]) groups[key] = []
		groups[key].push(report)
	}
	return Object.entries(groups)
}

async function fetchReports({ page, pageSize }) {
	try {
		const data = await getReports({ page, pageSize })

		return data
	} catch (error) {
		console.error('Error during get reports list', error)
		errorMessage({ content: 'Erro ao listar relatórios' })
	}
}

const Reports = () => {
	const [reportType, setReportType] = useState('documents')
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const queryClient = useQueryClient()

	const { data, isLoading } = useQuery({
		queryKey: ['reports', reportType, page, pageSize],
		queryFn: () => fetchReports({ page, pageSize }),
		staleTime: 1000 * 30,
		refetchOnWindowFocus: true,
		retry: false,
	})

	const reports = data?.reports
	const total = data?.total || 0

	const reprocessMutation = useMutation({
		mutationFn: (reportId) => reprocessReport(reportId),
		onSuccess: () => {
			successMessage({ content: 'Relatório reenviado para processamento' })
			queryClient.invalidateQueries({ queryKey: ['reports'] })
		},
		onError: (error) => {
			const status = error?.response?.status
			if (status === 429) {
				errorMessage({
					content: 'Aguarde 5 minutos antes de reprocessar este relatório',
				})
			} else {
				errorMessage({ content: 'Erro ao reprocessar relatório' })
			}
		},
	})

	const checkStatusMutation = useMutation({
		mutationFn: (reportId) => checkReportStatus(reportId),
		onSuccess: (data) => {
			if (data.report_status !== 'processing') {
				queryClient.invalidateQueries({ queryKey: ['reports'] })
			}
		},
		onError: () => {
			errorMessage({ content: 'Erro ao verificar status do relatório' })
		},
	})

	const handleDownload = (report) => {
		if (!report.file_url) return
		window.open(report.file_url, '_blank', 'noopener,noreferrer')
	}

	const handleRetry = (report) => {
		reprocessMutation.mutate(report.id)
	}

	const handleCheckStatus = (report) => {
		checkStatusMutation.mutate(report.id)
	}

	const renderContent = () => {
		if (isLoading) {
			return <Spin />
		}

		if (!reports?.length) {
			return <Empty description="Nenhum relatório encontrado" />
		}

		const reportsByDate = groupReportsByDate(reports)

		return (
			<>
				<div className={styles.dateGroups}>
					{reportsByDate.map(([dateLabel, groupReports]) => (
						<div key={dateLabel}>
							<p className={styles.dateLabel}>{dateLabel}</p>
							<ul className={styles.reportList}>
								{groupReports.map((report) => {
									const createdAt = formatDate(report.created_at)
									const name =
										report.file_name ||
										REPORT_TYPE_LABELS[report.report_type] ||
										'Relatório'

									return (
										<AttachmentPreview
											key={report.id}
											attachment={{
												id: report.id,
												original_name: name,
												file_size: formatFileSize(report.file_size),
												created_at: createdAt,
												responsible: report?.requested_by?.name,
											}}
											status={report.status}
											loading={
												(reprocessMutation.isPending &&
													reprocessMutation.variables === report.id) ||
												(checkStatusMutation.isPending &&
													checkStatusMutation.variables === report.id)
											}
											onDownload={() => handleDownload(report)}
											onRetry={() => handleRetry(report)}
											onCheckStatus={() => handleCheckStatus(report)}
										/>
									)
								})}
							</ul>
						</div>
					))}
				</div>
				{total > DEFAULT_PAGE_SIZE && (
					<div className={styles.pagination}>
						<Pagination
							current={page}
							pageSize={pageSize}
							total={total}
							onChange={(nextPage, nextSize) => {
								setPage(nextSize !== pageSize ? 1 : nextPage)
								setPageSize(nextSize)
							}}
							showSizeChanger
							pageSizeOptions={PAGE_SIZE_OPTIONS}
							locale={{ items_per_page: '' }}
						/>
					</div>
				)}
			</>
		)
	}

	return (
		<MainLayout selectedKey="reports">
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader>
					<Breadcrumb>
						<Breadcrumb.Item onClick={() => {}}>Relatórios</Breadcrumb.Item>
					</Breadcrumb>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff', padding: '0 24px 24px' }}>
					{renderContent()}
				</Layout>
			</Layout>
		</MainLayout>
	)
}

export default Reports
